import { createContext, useContext, ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { gql } from 'graphql-request'
import { gqlClient } from '@/lib/graphql'
import { getToken, saveToken, clearToken } from '@/lib/auth'

export interface Viewer {
  id: string
  email: string
  name: string | null
  avatarUrl: string | null
  githubLogin: string | null
  githubConnected: boolean
}

const VIEWER = gql`
  query Viewer {
    viewer {
      id
      email
      name
      avatarUrl
      githubLogin
      githubConnected
    }
  }
`

interface AuthContextValue {
  viewer: Viewer | null
  loading: boolean
  login: (token: string) => Promise<void>
  logout: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['viewer'],
    queryFn: async () => {
      if (!getToken()) return null
      const res = await gqlClient.request<{ viewer: Viewer | null }>(VIEWER)
      return res.viewer
    },
  })

  const login = async (token: string) => {
    saveToken(token)
    await queryClient.invalidateQueries({ queryKey: ['viewer'] })
  }

  const logout = () => {
    clearToken()
    queryClient.setQueryData(['viewer'], null)
  }

  return (
    <AuthContext.Provider value={{ viewer: data ?? null, loading: isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
